import React from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Link,
  Stack,
  styled,
  Typography
} from '@mui/material';
import { Box } from '@mui/system';
import {
  authService,
  fields,
  imageUtil,
  ResponsiveImage,
  routeDirectory,
  validationUtil
} from 'modules';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';



const imgsAuthBackground = imageUtil.getUrlSet('auth_background', 'png');
const imgsSuperAdminAuthBackground = imageUtil.getUrlSet('super_admin_auth_background', 'png');
const imgsLogo = imageUtil.getUrlSet('logo', 'png');
const imgsEmail = imageUtil.getUrlSet('email', 'png');
const imgsPassword = imageUtil.getUrlSet('password', 'png');



const __formSchema = yup.object({
  email: validationUtil.emailSchema.required('Email is required.'),
  password: validationUtil.passwordSchema.required('Password is required.')
}).required();

type IFormData = yup.InferType<typeof __formSchema>;

const ControlledTextField = fields.genTextFieldControllerComponent<IFormData>();



const RootContainerBox = styled(Box)(() => ({
  display: 'flex',
  height: '100vh',
  alignItems: 'stretch'
}));

const FormContainerBox = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  flexDirection: 'column',
  minWidth: 400,
  marginTop: 100,
  marginBottom: 100
}));

const SignUpNowBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  marginTop: theme.spacing(2)
}));

const LeftPaneBox = styled(Box)(({ theme }) => ({
  flex: 1,
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'stretch'
}));

const RightPaneBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  height: '100vh',
  maxWidth: 720,
  '& img': {
    width: '100%',
    height: 'inherit',
    objectFit: 'cover'
  }
}));

const LogoBox = styled(Box)({
  marginBottom: 20
});

const SignUpLink = styled(Link, {
  shouldForwardProp: prop => prop !== 'loading'
})<{ loading: boolean; }>(({ loading, theme }) => ({
  pointerEvents: loading ? 'none' : 'auto',
  marginLeft: theme.spacing(1)
}));



export interface IAuthenticationActivityProps {
  superAdmin?: boolean;
}

export const AuthenticationActivity: React.FC<IAuthenticationActivityProps> = ({ superAdmin }) => {
  const navigate = useNavigate();

  const [loading, setLoading] = React.useState(false);

  const form = useForm({
    resolver: yupResolver(__formSchema),
    shouldFocusError: true,
    defaultValues: {
      email: '',
      password: ''
    }
  });



  React.useEffect(() => {
    const subscription = authService.getAuthStateObservable().subscribe(authState => {
      if (authState.status === 'loggedIn') {
        navigate(routeDirectory.home.path());
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);



  async function processSubmit(data: IFormData) {
    setLoading(true);
    try {
      await authService.logIn({
        email: data.email,
        password: data.password
      });

    } catch (e) {
      if ((e as Error).name === 'UserNotFoundException') {
        alert('User does not exist.');

      } else if ((e as Error).name === 'UserNotConfirmedException') {
        alert('User is not confirmed. Please enter your confirm code again.');
        navigate(routeDirectory.authentication.confirmCode.path(data.email));

      } else {
        alert(e);
      }
    } finally {
      setLoading(false);
    }
  }



  return (
    <RootContainerBox>
      <LeftPaneBox>
        <FormContainerBox>
          <LogoBox>
            <ResponsiveImage.FC urlSet={imgsLogo} />
          </LogoBox>

          <div>
            <form onSubmit={form.handleSubmit(processSubmit)}>
              <Stack spacing={4}>
                <Stack spacing={2}>
                  <Typography variant="h1">{superAdmin ? 'Super Admin Panel' : 'Welcome!'}</Typography>
                  <Typography>Please log in to your account.</Typography>
                </Stack>

                <ControlledTextField
                  startAdornment={<ResponsiveImage.FC urlSet={imgsEmail} />}
                  formControl={form.control}
                  isRequired
                  label="Email"
                  fieldKey="email" />

                <ControlledTextField
                  startAdornment={<ResponsiveImage.FC urlSet={imgsPassword} />}
                  isRequired
                  label="Password"
                  formControl={form.control}
                  fieldKey="password"
                  type="password" />

                <Stack direction="row" justifyContent="flex-end">
                  <Link
                    style={{ pointerEvents: loading ? 'none' : 'auto' }}
                    href={routeDirectory.authentication.forgotPassword.path()}>
                    Forgot Password?
                  </Link>
                </Stack>

                <Button
                  type="submit"
                  disabled={loading}>
                  Login
                </Button>
              </Stack>
            </form>

            {!superAdmin ? (
              <SignUpNowBox>
                <Typography>
                  Don't have an account?
                </Typography>

                <SignUpLink
                  loading={loading}
                  href={routeDirectory.authentication.signup.path()}>
                  Sign up now
                </SignUpLink>
              </SignUpNowBox>
            ) : <div />}
          </div>

          <div />
        </FormContainerBox>
      </LeftPaneBox>

      <RightPaneBox>
        <ResponsiveImage.FC urlSet={superAdmin ? imgsSuperAdminAuthBackground : imgsAuthBackground} />
      </RightPaneBox>
    </RootContainerBox>
  );
};
